import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'

import Avatar from '../../components/Avatar'
import Icon from '../../components/Icon'
import { equalShares, findPerson, formatMoney, formatShortDate, groupBalanceByPerson, groupSpent, type Balance, type Group, type Overview } from '../../lib/ledger'


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.5rem",
    fontSize: "13px",
    fontWeight: 600,
    color: "#5b7460",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      color: "#307241",
    },
  },
  s2: {
    height: "16px",
    width: "16px",
  },
  s3: {
    overflow: "hidden",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#dce7da",
    backgroundImage: "linear-gradient(125deg,#f6fbf4,#e6f1e4)",
    padding: "20px",
    "@media (min-width: 640px)": {
      padding: "28px",
    },
  },
  s4: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      alignItems: "start",
    },
  },
  s5: {
    display: "flex",
    minWidth: "0px",
    gap: "16px",
  },
  s6: {
    display: "flex",
    height: "48px",
    width: "48px",
    flexShrink: "0",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "1rem",
    backgroundColor: "#4f9360",
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  },
  s7: {
    height: "24px",
    width: "24px",
  },
  s8: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#54805e",
  },
  s9: {
    marginTop: "4px",
    fontSize: "clamp(1.7rem,3vw,2.15rem)",
    fontWeight: 600,
    letterSpacing: "-.045em",
  },
  s10: {
    marginTop: "8px",
    maxWidth: "36rem",
    fontSize: "13px",
    color: "#657168",
  },
  s11: {
    display: "flex",
    height: "40px",
    flexShrink: "0",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c49",
    },
  },
  s12: {
    marginTop: "24px",
    display: "grid",
    maxWidth: "36rem",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    borderTopColor: "#d7e4d4",
    borderRadius: "0.75rem",
    backgroundColor: "color-mix(in srgb, #fff 65%, transparent)",
    paddingBlock: "12px",
  },
  s13: {
    paddingInline: "16px",
  },
  s14: {
    fontSize: "11px",
    color: "#708073",
  },
  s15: {
    marginTop: "4px",
    fontSize: "17px",
    fontWeight: 600,
    color: "#3a5c41",
  },
  s16: {
    display: "flex",
    gap: "4px",
    overflowX: "auto",
    borderBottomWidth: 1,
    borderColor: "#e2e8e0",
  },
  s17: {
    overflow: "hidden",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e2e8e0",
    backgroundColor: "#fff",
    boxShadow: "0 4px 16px rgba(48,70,51,.035)",
  },
  s18: {
    paddingInline: "20px",
    paddingBlock: "56px",
    textAlign: "center",
  },
  s19: {
    marginInline: "auto",
    display: "flex",
    height: "44px",
    width: "44px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "1rem",
    backgroundColor: "#edf6eb",
    color: "#4d8959",
  },
  s20: {
    height: "20px",
    width: "20px",
  },
  s21: {
    marginTop: "16px",
    fontSize: "14px",
    fontWeight: 600,
  },
  s22: {
    marginTop: "12px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#3e7d4d",
    ":hover": {
      textDecoration: "underline",
    },
  },
  s23: { borderTopWidth: 1, borderTopColor: "#edf0eb" },
  s24: {
    display: "flex",
    gap: "12px",
    paddingInline: "16px",
    paddingBlock: "16px",
    "@media (min-width: 640px)": {
      paddingInline: "20px",
    },
  },
  s25: {
    display: "flex",
    height: "40px",
    width: "40px",
    flexShrink: "0",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.75rem",
    backgroundColor: "#eef6eb",
    color: "#498756",
  },
  s26: {
    minWidth: "0px",
    flex: "1 1 0%",
  },
  s27: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "start",
    justifyContent: "space-between",
    columnGap: "16px",
    rowGap: "4px",
  },
  s28: {
    fontSize: "14px",
    fontWeight: 600,
  },
  s29: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#314d39",
  },
  s30: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#748077",
  },
  s31: {
    marginTop: "8px",
    fontSize: "11px",
    color: "#838d84",
  },
  s32: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e2e8e0",
    backgroundColor: "#fff",
    padding: "16px",
    boxShadow: "0 4px 16px rgba(48,70,51,.035)",
    "@media (min-width: 640px)": {
      padding: "20px",
    },
  },
  s33: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
  },
  s34: {
    fontSize: "16px",
    fontWeight: 600,
  },
  s35: {
    height: "20px",
    width: "20px",
    color: "#578561",
  },
  s36: {
    marginTop: "20px",
    borderTopWidth: 1,
    borderTopColor: "#edf0eb",
  },
  s37: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    paddingBlock: "12px",
  },
  s38: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 600,
  },
  s39: {
    textAlign: "right",
  },
  s40: {
    marginTop: "4px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#3b7b4d",
    ":hover": {
      textDecoration: "underline",
    },
  },
  s41: {
    marginTop: "20px",
    display: "grid",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s42: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#e4e9e2",
    padding: "12px",
  },
  s43: {
    minWidth: "0px",
  },
  s44: {
    marginTop: "2px",
    fontSize: "11px",
    color: "#7b857d",
  },
})

const groupDetailStyles = stylex.create({
  tab: { flexShrink: 0, borderBottomWidth: 2, paddingInline: '16px', paddingBottom: '12px', paddingTop: '4px', fontSize: '13px', fontWeight: 600, textTransform: 'capitalize', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  tabActive: { borderColor: '#4b9160', color: '#367745' },
  tabIdle: { borderColor: 'transparent', color: '#7a857c', ':hover': { color: '#44664c' } },
  balanceLabel: { marginTop: '2px', fontSize: '11px' },
  positiveLabel: { color: '#397a4b' },
  negativeLabel: { color: '#a5501d' },
  zeroLabel: { color: '#778077' },
  balanceAmount: { fontSize: '13px', fontWeight: 600 },
  positiveAmount: { color: '#2d7943' },
  negativeAmount: { color: '#a44f1c' },
  zeroAmount: { color: '#667168' },
})
type GroupDetailPageProps = {
  data: Overview
  group: Group
  onAddExpense: (groupID: string) => void
  onBack: () => void
  onSettle: (balance: Balance) => void
}

type GroupTab = 'expenses' | 'balances' | 'members'

export default function GroupDetailPage({ data, group, onAddExpense, onBack, onSettle }: GroupDetailPageProps) {
  const [tab, setTab] = useState<GroupTab>('expenses')
  const expenses = data.expenses.filter((expense) => expense.groupId === group.id)
  const members = group.memberIds.map((id) => findPerson(data, id)).filter((person) => person !== undefined)
  const balances = groupBalanceByPerson(data, group.id)
  const groupSpentTotal = groupSpent(data, group.id)

  return <div {...stylex.props(styles.s0)}><button {...stylex.props(styles.s1)} onClick={onBack} type="button"><Icon {...stylex.props(styles.s2)} name="arrow-left" />All groups</button><header {...stylex.props(styles.s3)}><div {...stylex.props(styles.s4)}><div {...stylex.props(styles.s5)}><span {...stylex.props(styles.s6)}><Icon {...stylex.props(styles.s7)} name="groups" /></span><div><p {...stylex.props(styles.s8)}>{group.memberIds.length} people</p><h1 {...stylex.props(styles.s9)}>{group.name}</h1><p {...stylex.props(styles.s10)}>{group.description || 'Shared expenses in one place.'}</p></div></div><button {...stylex.props(styles.s11)} onClick={() => onAddExpense(group.id)} type="button"><Icon {...stylex.props(styles.s2)} name="plus" />Add expense</button></div><div {...stylex.props(styles.s12)}><div {...stylex.props(styles.s13)}><p {...stylex.props(styles.s14)}>Total spent</p><p {...stylex.props(styles.s15)}>{formatMoney(groupSpentTotal, data.currentUser.currency)}</p></div><div {...stylex.props(styles.s13)}><p {...stylex.props(styles.s14)}>Expenses</p><p {...stylex.props(styles.s15)}>{expenses.length}</p></div></div></header>
    <div {...stylex.props(styles.s16)} role="tablist">{(['expenses', 'balances', 'members'] as GroupTab[]).map((item) => <button aria-selected={tab === item} {...stylex.props(groupDetailStyles.tab, tab === item ? groupDetailStyles.tabActive : groupDetailStyles.tabIdle)} key={item} onClick={() => setTab(item)} role="tab" type="button">{item}</button>)}</div>
    {tab === 'expenses' && <section {...stylex.props(styles.s17)}>{expenses.length === 0 ? <div {...stylex.props(styles.s18)}><span {...stylex.props(styles.s19)}><Icon {...stylex.props(styles.s20)} name="receipt" /></span><p {...stylex.props(styles.s21)}>No expenses yet</p><button {...stylex.props(styles.s22)} onClick={() => onAddExpense(group.id)} type="button">Add the first expense</button></div> : <div {...stylex.props(styles.s23)}>{expenses.map((expense) => { const payer = findPerson(data, expense.paidById); const shares = equalShares(expense.amountCents, expense.participantIds.length); const yourIndex = expense.participantIds.indexOf(data.currentUser.id); const yourShare = shares[yourIndex] ?? 0; return <article {...stylex.props(styles.s24)} key={expense.id}><span {...stylex.props(styles.s25)}><Icon {...stylex.props(styles.s20)} name="receipt" /></span><div {...stylex.props(styles.s26)}><div {...stylex.props(styles.s27)}><h2 {...stylex.props(styles.s28)}>{expense.description}</h2><p {...stylex.props(styles.s29)}>{formatMoney(expense.amountCents, expense.currency)}</p></div><p {...stylex.props(styles.s30)}>{expense.paidById === data.currentUser.id ? 'You paid' : `${payer?.name ?? 'Someone'} paid`} · {formatShortDate(expense.createdAt)}</p><p {...stylex.props(styles.s31)}>Split between {expense.participantIds.length} people · Your share {formatMoney(yourShare, expense.currency)}</p></div></article> })}</div>}</section>}
    {tab === 'balances' && <section {...stylex.props(styles.s32)}><div {...stylex.props(styles.s33)}><div><h2 {...stylex.props(styles.s34)}>Balances in this group</h2><p {...stylex.props(styles.s30)}>Settlement records are shown across all your groups.</p></div><Icon {...stylex.props(styles.s35)} name="settle" /></div><div {...stylex.props(styles.s36)}>{group.memberIds.filter((id) => id !== data.currentUser.id).map((memberID) => { const person = findPerson(data, memberID); const amount = balances.get(memberID) ?? 0; const globalBalance = data.balances.find((balance) => balance.personId === memberID); if (!person) return null; return <div {...stylex.props(styles.s37)} key={memberID}><Avatar person={person} size={38} /><div {...stylex.props(styles.s26)}><p {...stylex.props(styles.s38)}>{person.name}</p><p {...stylex.props(groupDetailStyles.balanceLabel, amount > 0 ? groupDetailStyles.positiveLabel : amount < 0 ? groupDetailStyles.negativeLabel : groupDetailStyles.zeroLabel)}>{amount === 0 ? 'All settled up in this group' : amount > 0 ? 'owes you in this group' : 'you owe in this group'}</p></div><div {...stylex.props(styles.s39)}><p {...stylex.props(groupDetailStyles.balanceAmount, amount > 0 ? groupDetailStyles.positiveAmount : amount < 0 ? groupDetailStyles.negativeAmount : groupDetailStyles.zeroAmount)}>{amount === 0 ? '—' : formatMoney(Math.abs(amount), data.currentUser.currency)}</p>{globalBalance && <button {...stylex.props(styles.s40)} onClick={() => onSettle(globalBalance)} type="button">Settle total</button>}</div></div> })}</div></section>}
    {tab === 'members' && <section {...stylex.props(styles.s32)}><h2 {...stylex.props(styles.s34)}>People in {group.name}</h2><p {...stylex.props(styles.s30)}>Everyone included when you split a group expense.</p><div {...stylex.props(styles.s41)}>{members.map((person) => person && <div {...stylex.props(styles.s42)} key={person.id}><Avatar person={person} size={40} /><div {...stylex.props(styles.s43)}><p {...stylex.props(styles.s38)}>{person.id === data.currentUser.id ? 'You' : person.name}</p><p {...stylex.props(styles.s44)}>{person.id === data.currentUser.id ? 'Group member' : 'Can add and split expenses'}</p></div></div>)}</div></section>}
  </div>
}
